import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FadeImage } from '@/components/ui/FadeImage'
import Link from '@/routing/Link'
import Image from '@/routing/Image'
import { Bookmark, Check, Loader2, Lock, MapPin, Users } from 'lucide-react'
import { PublisherPill } from '@/components/audiences/PublisherPill'
import { Badge } from '@/components/ui/badge'
import { CardErrorBadge } from '@/components/ui/CardErrorBadge'
import { Heading } from '@/components/ui/Heading'
import { cn } from '@/lib/utils'
import { audienceDisplayName } from '@/lib/audience-display-name'
import { audienceTierDisplayName } from '@/lib/billing/display-names'
import { CREDITS_PER_RESPONDENT, CHAT_CREDITS_PER_MSG, ROOM_CREDITS_PER_MSG } from '@/lib/billing/credit-rates'
import { CreditIcon } from '@/components/icons/CreditIcon'
import { FALLBACK_DISPLAY_FONT_CLASS } from '../utils/display-fonts'
import { getDisplayFontConfig, responsiveFontVars } from '../utils/font-config'
import {
  resolveAudienceCanonicalCoverUrl,
  resolveAudienceTitleFont,
  resolveAudienceDigitalTwinCount,
} from '../utils/audience-presentation'
import { getIndustryDisplayName, getIndustryIcon } from '@/lib/constants/industry-categories'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

export type AudienceAccessType = 'private' | 'shared'

export interface CinematicAudienceCardProps {
  audience: {
    id: string
    name: string
    coverImageUrl?: string | null
    aiDescription?: string | null
    contextText?: string | null
    description?: string | null
    audienceType?: 'B2B' | 'B2C' | null
    tier?: 'tier1' | 'tier2' | 'tier3' | 'standard' | 'premium'
    sharingMode?: string
    status?: string
    processingError?: string | null
    rowCount?: number | null
    personaCount?: number | null
    columnCount?: number | null
    /** @deprecated Use contentLabels instead. */
    aiTags?: string[]
    contentLabels?: Array<{ token: string; name: string; fullPath: string; tier: number }>
    industryLabel?: {
      token: string
      name: string
      fullPath: string
      tier: number
      confidence: number | null
    } | null
    createdAt?: string
    canEdit?: boolean
    creatorName?: string | null
    creatorEmail?: string | null
    /** Credit rates the audience list returns; the tier table is the fallback. */
    creditsPerRespondent?: number | null
    chatCreditsPerMessage?: number | null
    focusGroupCreditsPerMessage?: number | null
    publisherDomain?: string | null
    organizationName?: string | null
    logoUrl?: string | null
    isBookmarked?: boolean
    stageMetadata?: {
      tagline?: string | null
      titleFont?: string | null
      selectedCoverPhotoIndex?: number | null
      coverPhotoOptions?: Array<{ url: string; thumbnailUrl?: string }> | null
    } | null
    narrativeCard?: {
      header?: string;
      subheader: string;
      [key: string]: unknown;
    } | null
    constantContext?: Record<string, string>
    audienceLabels?: Array<{ token: string; name: string; fullPath: string; tier: number }>
  }
  provider?: {
    name: string
    logoUrl?: string | null
  } | null
  /** @deprecated Prefer audience.sharingMode — kept for backward compatibility. */
  accessType?: AudienceAccessType
  formatRelativeTime: (date: string) => string
  actionMenu?: React.ReactNode
  onClick?: () => void
  selected?: boolean
  onBookmarkToggle?: (id: string, nextBookmarked: boolean) => Promise<void>
  /** When set, shows a "Managed by {name}" sub-label for cross-org shared audiences */
  sharedByOrgName?: string | null
}

const regionDisplayNames = new Intl.DisplayNames(['en'], { type: 'region' })

function countryName(code: string): string {
  try {
    return regionDisplayNames.of(code) ?? code
  } catch {
    return code
  }
}

/** Treat null, undefined, and whitespace-only as missing (URLs must not use '' to block fallback). */
function nonemptyString(s: string | null | undefined): string | null {
  const t = s?.trim()
  return t ? t : null
}

function CinematicAudienceCard({
  audience,
  provider,
  accessType,
  formatRelativeTime,
  actionMenu,
  onClick,
  selected,
  onBookmarkToggle,
  sharedByOrgName,
}: CinematicAudienceCardProps) {
  const { t, i18n } = useTranslation('app')
  const isProcessing = audience.status === 'processing'
  const isRelaunching = audience.status === 'relaunching'
  const hasError = audience.status === 'error'
  // While relaunching, the prior audience data is still live → keep clickable.
  const canViewDetails = !isProcessing

  const [bookmarkLoading, setBookmarkLoading] = useState(false)

  const handleBookmarkClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!onBookmarkToggle || bookmarkLoading) return
    setBookmarkLoading(true)
    try {
      await onBookmarkToggle(audience.id, !audience.isBookmarked)
    } finally {
      setBookmarkLoading(false)
    }
  }

  const wizardTagline = audience.stageMetadata?.tagline?.trim() || null

  const tagline = wizardTagline ?? ''

  const categoryParts: string[] = []
  if (audience.audienceType) categoryParts.push(audience.audienceType)
  const tierLabel = audience.tier
    ? audienceTierDisplayName(audience.tier)
    : t('billing.audienceTiers.basic')
  const categoryText = categoryParts.join(' · ')

  const isMarketplace = audience.sharingMode === 'marketplace'
  const pillLabel =
    nonemptyString(audience.organizationName)
    ?? nonemptyString(provider?.name)
    ?? (isMarketplace ? nonemptyString(audience.publisherDomain) : null)
    ?? ''
  const pillLogoUrl =
    nonemptyString(audience.logoUrl)
    ?? nonemptyString(provider?.logoUrl)
    ?? null

  const resolvedCoverImage = resolveAudienceCanonicalCoverUrl(audience)
  const resolvedTitleFont = resolveAudienceTitleFont(audience)
  const titleFontFamily = resolvedTitleFont
    ? `'${resolvedTitleFont}', sans-serif`
    : undefined
  const fontConfig = getDisplayFontConfig(resolvedTitleFont)
  const digitalTwinCount = resolveAudienceDigitalTwinCount(audience)

  // Industry label — single pill from industryLabel, fallback to first contentLabel
  const industryDisplay = audience.industryLabel
    ? {
        key: audience.industryLabel.token,
        name: getIndustryDisplayName(audience.industryLabel.name),
        icon: getIndustryIcon(audience.industryLabel.fullPath.split(' | ')[0]),
        fullPath: audience.industryLabel.fullPath,
      }
    : (audience.contentLabels?.[0]
      ? {
          key: audience.contentLabels[0].token,
          name: getIndustryDisplayName(audience.contentLabels[0].name),
          icon: getIndustryIcon(audience.contentLabels[0].name),
          fullPath: audience.contentLabels[0].fullPath,
        }
      : null)

  const countryLabels = (audience.audienceLabels ?? [])
    .filter((l) => l.tier === 6 && l.fullPath.includes('Country Extension'))
    .map((l) => l.name)
    .filter((v, i, arr) => arr.indexOf(v) === i)

  const resolvedCountryNames: Array<string> = countryLabels.length > 0
    ? countryLabels.map((code) => countryName(code))
    : (() => {
        const raw = audience.constantContext?.['std_geo_country']?.trim()
        if (!raw) return []
        return [raw.length === 2 ? countryName(raw) : raw]
      })()

  const inner = (
    <div
      className={cn(
        'relative overflow-hidden w-full h-[340px] rounded-[12px]',
        'transition-[transform,box-shadow] duration-300 ease-out group',
        canViewDetails && 'hover:-translate-y-1',
        selected
          ? 'border-2 border-foreground dark:border-cta'
          : 'border-2 border-transparent',
        hasError && 'ring-2 ring-destructive/30',
        isProcessing && 'ring-2 ring-primary/20',
      )}
      style={{ boxShadow: 'var(--cinematic-card-shadow, none)' }}
    >
      {/* Background image */}
      <div className="absolute inset-0 bg-muted pointer-events-none" />
      {resolvedCoverImage && (
        <FadeImage
          src={resolvedCoverImage}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover object-center transition-[transform,opacity] duration-500 ease-out group-hover:scale-105 pointer-events-none"
        />
      )}

      {/* Gradient overlay — darkens on hover to support revealed content */}
      <div
        className="absolute inset-0 transition-opacity duration-300 group-hover:opacity-100 pointer-events-none"
        style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.1) 40%, rgba(0,0,0,0) 60%)' }}
      />

      {/* Top-left badge: error replaces sharing indicator; otherwise sharing capability */}
      <div className="absolute top-3 left-3 z-10">
        {hasError ? (
          <CardErrorBadge dataTestId="audience-error-badge" />
        ) : (() => {
          const mode = audience.sharingMode ?? (accessType === 'shared' ? 'shared' : 'private')

          if (mode === 'marketplace') {
            return <PublisherPill icon={pillLogoUrl} name={pillLabel} />
          }

          if (mode === 'shared' || mode === 'organization') {
            const sharedByName = !audience.canEdit
              ? (audience.creatorName?.trim() || audience.creatorEmail?.trim() || null)
              : null
            if (sharedByName) {
              return (
                <div className="flex items-center gap-1.5 bg-black/40 backdrop-blur-[12px] px-2.5 py-1 rounded-full">
                  <Users className="h-3.5 w-3.5 text-white/90 shrink-0" />
                  <span className="text-xs font-medium text-white/90 truncate max-w-[140px]">{sharedByName}</span>
                </div>
              )
            }
            return (
              <div className="flex items-center justify-center h-7 w-7 rounded-full bg-black/40 backdrop-blur-sm border border-white/20">
                <Users className="h-3 w-3 text-white" />
              </div>
            )
          }

          return (
            <div
              data-testid="audience-sharing-badge"
              className="flex items-center justify-center h-7 w-7 rounded-full bg-black/40 backdrop-blur-sm border border-white/20"
            >
              <Lock className="h-3 w-3 text-white" />
            </div>
          )
        })()}
      </div>

      {/* Top-right: bookmark, status, and action menu (hover) */}
      <div className="absolute top-3 right-3 z-20 flex items-center justify-end gap-1.5">
        {onBookmarkToggle && audience.status === 'ready' && (
          <button
            type="button"
            aria-label={audience.isBookmarked ? 'Remove bookmark' : 'Bookmark audience'}
            data-testid={`bookmark-button-${audience.id}`}
            disabled={bookmarkLoading}
            onClick={handleBookmarkClick}
            className={cn(
              'flex items-center justify-center size-[26px] rounded-full transition-colors',
              'bg-black/40 backdrop-blur-sm border border-white/20',
              'opacity-0 group-hover:opacity-100',
              audience.isBookmarked && 'opacity-100',
              bookmarkLoading && 'cursor-wait',
            )}
          >
            {bookmarkLoading ? (
              <Loader2 className="size-[13px] text-white animate-spin" />
            ) : (
              <Bookmark
                className={cn(
                  'size-[13px]',
                  audience.isBookmarked
                    ? 'text-[hsl(var(--platform-card))] fill-[hsl(var(--platform-card))]'
                    : 'text-white',
                )}
              />
            )}
          </button>
        )}

        {selected && (
          <div className="flex items-center justify-center size-[26px] rounded-full bg-foreground text-background dark:bg-cta dark:text-cta-foreground">
            <Check className="size-[14px]" strokeWidth={2.5} />
          </div>
        )}

        {!selected && (isProcessing || isRelaunching) && (
          <Badge className="bg-black/40 backdrop-blur-sm text-white border-0 rounded-full text-[11px] flex items-center gap-1 shadow-sm">
            <Loader2 className="h-3 w-3 animate-spin" />
            {isRelaunching ? 'Reprocessing' : 'Processing'}
          </Badge>
        )}

        {actionMenu && (
          <div
            className="opacity-0 transition-opacity duration-200 group-hover:opacity-100"
            onClick={(e) => { e.preventDefault(); e.stopPropagation() }}
          >
            {actionMenu}
          </div>
        )}
      </div>

      {/* Content — bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-3 text-white z-10 flex flex-col justify-end">
        {/* Title — unique display font */}
        <h3
          className={cn(
            titleFontFamily ? undefined : FALLBACK_DISPLAY_FONT_CLASS,
            'text-[length:var(--card-fs-sm)] lg:text-[length:var(--card-fs)]',
            'text-white tracking-tight line-clamp-2 pb-[2px]',
          )}
          style={responsiveFontVars(fontConfig.card, 'card', titleFontFamily)}
        >
          {audienceDisplayName(audience.name)}
        </h3>

        {/* Always visible: tagline + category + meta pills */}
        {tagline && (
          <p className="text-sm font-medium font-sans text-white/90 mt-1.5 line-clamp-2">
            {tagline}
          </p>
        )}

        {/* Meta row: industry pills + country + type + tier with credit tooltip */}
        <TooltipProvider delayDuration={0}>
          <div className="flex w-full min-w-0 items-center gap-1.5 mt-2 flex-wrap">
            {industryDisplay && (() => {
              const Icon = industryDisplay.icon
              const tooltipPath = industryDisplay.fullPath.replace(/\s\|\s/g, ' > ')
              return (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 rounded-full bg-white/15 backdrop-blur-sm px-2 py-0.5 text-[11px] font-medium text-white/90 cursor-default">
                      <Icon className="h-3 w-3" />
                      {industryDisplay.name}
                    </span>
                  </TooltipTrigger>
                  {industryDisplay.fullPath !== industryDisplay.name && (
                    <TooltipContent
                      side="top"
                      className="z-[9999] max-w-xs text-xs bg-tooltip text-tooltip-foreground shadow-lg border border-border dark:border-transparent"
                    >
                      {tooltipPath}
                    </TooltipContent>
                  )}
                </Tooltip>
              )
            })()}
            {resolvedCountryNames.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/15 backdrop-blur-sm px-2 py-0.5 text-[11px] font-medium text-white/90 cursor-default">
                    <MapPin className="h-3 w-3" />
                    {resolvedCountryNames[0]}
                    {resolvedCountryNames.length > 1 && (
                      <span className="inline-flex items-center rounded-full bg-white/20 px-1.5 py-px text-[10px] font-semibold text-white/90">
                        +{resolvedCountryNames.length - 1}
                      </span>
                    )}
                  </span>
                </TooltipTrigger>
                {resolvedCountryNames.length > 1 && (
                  <TooltipContent
                    side="top"
                    className="z-[9999] max-w-xs text-xs bg-tooltip text-tooltip-foreground shadow-lg border border-border dark:border-transparent"
                  >
                    {resolvedCountryNames.join(', ')}
                  </TooltipContent>
                )}
              </Tooltip>
            )}
            {audience.audienceType && (
              <span className="inline-flex items-center rounded-full bg-white/15 backdrop-blur-sm px-2 py-0.5 text-[11px] font-medium text-white/90">
                {audience.audienceType}
              </span>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center gap-1 rounded-full bg-white/15 backdrop-blur-sm px-2 py-0.5 text-[11px] font-medium text-white/90 cursor-default">
                  <CreditIcon className="h-3 w-3" />
                  {tierLabel}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="p-3 bg-popover text-popover-foreground">
                <div className="space-y-1.5 text-xs">
                  <div className="flex items-center justify-between gap-6">
                    <span className="text-muted-foreground">{t('audiences.creditStudy')}</span>
                    <span className="font-medium flex items-center gap-1"><CreditIcon className="h-3 w-3" />{audience.creditsPerRespondent ?? CREDITS_PER_RESPONDENT[audience.tier ?? 'tier1'] ?? 1}</span>
                  </div>
                  <div className="flex items-center justify-between gap-6">
                    <span className="text-muted-foreground">{t('audiences.creditInterview')}</span>
                    <span className="font-medium flex items-center gap-1"><CreditIcon className="h-3 w-3" />{audience.chatCreditsPerMessage ?? CHAT_CREDITS_PER_MSG[audience.tier ?? 'tier1'] ?? 1}{t('audiences.perMsg')}</span>
                  </div>
                  <div className="flex items-center justify-between gap-6">
                    <span className="text-muted-foreground">{t('audiences.creditFocusGroup')}</span>
                    <span className="font-medium flex items-center gap-1"><CreditIcon className="h-3 w-3" />{audience.focusGroupCreditsPerMessage ?? ROOM_CREDITS_PER_MSG[audience.tier ?? 'tier1'] ?? 2}{t('audiences.perMsg')}</span>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
            {digitalTwinCount != null && (
              <span className="shrink-0 whitespace-nowrap text-[11px] text-white/50 font-normal">
                {t('audiences.twins', {
                  count: digitalTwinCount,
                  formatted: digitalTwinCount.toLocaleString(i18n.language),
                })}
              </span>
            )}
          </div>
        </TooltipProvider>

        {/* Cross-org: "Managed by {OrgName}" persistent sub-label */}
        {sharedByOrgName && (
          <p
            className="text-xs text-white/60 mt-1 truncate"
            data-testid="managed-by-label"
          >
            {t('audiences.managedBy', { org: sharedByOrgName })}
          </p>
        )}

        {/* Hover-reveal: status messages + persona/field counts */}
        <div className="max-h-0 overflow-hidden opacity-0 group-hover:max-h-40 group-hover:opacity-100 transition-all duration-300 ease-out">
          {isRelaunching && (
            <p className="text-sm font-medium text-white/70 mt-1">
              Reprocessing — current audience stays live until the new run
              completes.
            </p>
          )}
        </div>
      </div>
    </div>
  )

  if (onClick) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
        className="block text-left w-full cursor-pointer"
      >
        {inner}
      </div>
    )
  }

  if (canViewDetails) {
    return (
      <Link href={`/audiences/${audience.id}`} className="block w-full">
        {inner}
      </Link>
    )
  }

  return inner
}

function arePropsEqual(prev: CinematicAudienceCardProps, next: CinematicAudienceCardProps) {
  const a = prev.audience
  const b = next.audience
  return (
    a.id === b.id &&
    a.status === b.status &&
    a.name === b.name &&
    resolveAudienceCanonicalCoverUrl(a) === resolveAudienceCanonicalCoverUrl(b) &&
    a.aiDescription === b.aiDescription &&
    a.contextText === b.contextText &&
    a.description === b.description &&
    a.sharingMode === b.sharingMode &&
    a.rowCount === b.rowCount &&
    a.personaCount === b.personaCount &&
    a.columnCount === b.columnCount &&
    a.tier === b.tier &&
    a.logoUrl === b.logoUrl &&
    a.publisherDomain === b.publisherDomain &&
    a.organizationName === b.organizationName &&
    a.creatorName === b.creatorName &&
    a.canEdit === b.canEdit &&
    resolveAudienceTitleFont(a) === resolveAudienceTitleFont(b) &&
    a.narrativeCard?.header === b.narrativeCard?.header &&
    (a.contentLabels ?? a.aiTags ?? []).length === (b.contentLabels ?? b.aiTags ?? []).length &&
    a.industryLabel?.token === b.industryLabel?.token &&
    a.constantContext?.['std_geo_country'] === b.constantContext?.['std_geo_country'] &&
    (a.audienceLabels?.length ?? 0) === (b.audienceLabels?.length ?? 0) &&
    a.isBookmarked === b.isBookmarked &&
    prev.selected === next.selected &&
    prev.accessType === next.accessType &&
    prev.provider?.name === next.provider?.name &&
    prev.provider?.logoUrl === next.provider?.logoUrl &&
    prev.onBookmarkToggle === next.onBookmarkToggle &&
    prev.sharedByOrgName === next.sharedByOrgName
  )
}

export default memo(CinematicAudienceCard, arePropsEqual)
